import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'UI';

const { Box, Select, TextField, MultiSelect, DateRangePicker } = vp;

export type Currency = 'all' | 'CAD' | 'USD' | '';
export type StatusFilter = 'all' | 'partiallyPaid' | 'open' | '';
export type PaymentChannel = 'all' | 'vopay' | 'erp' | '';
export type DateFilterType = 'dueDate' | 'closedDate';
export type TimeRange =
  | 'custom'
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisQuarter'
  | 'lastQuarter'
  | 'last6Months'
  | 'last12Months';

export type SearchParams = {
  type: string;
  currency: Currency;
  searchTerm: string;
  timeRange: TimeRange;
  startDate: vp.Timestamp | undefined;
  endDate: vp.Timestamp | undefined;
  status?: StatusFilter;
  paymentChannel?: PaymentChannel;
  dateFilterType?: DateFilterType;
};

type SearchFormProps = {
  type?: string;
  currency?: Currency;
  searchTerm?: string;
  timeRange?: TimeRange;
  startDate?: vp.Timestamp | undefined;
  endDate?: vp.Timestamp | undefined;
  status?: StatusFilter;
  paymentChannel?: PaymentChannel;
  dateFilterType?: DateFilterType;
  activeTab?: 'open' | 'closed';
  country: string;
  section: 'Payable' | 'Receivable';
  onSearch: (params: SearchParams) => void;
};

type FC = FunctionComponent<SearchFormProps>;

const AccountingSystemSearchForm: FC = (props: SearchFormProps) => {
  const {
    type: typeFromProps = '',
    currency: currencyFromProps = '',
    searchTerm: searchTermFromProps = '',
    timeRange: timeRangeFromProps = 'custom',
    startDate: startDateFromProps,
    endDate: endDateFromProps,
    status: statusFromProps = '',
    paymentChannel: paymentChannelFromProps = '',
    dateFilterType: dateFilterTypeFromProps = 'closedDate',
    activeTab = 'open',
    onSearch,
    section,
  } = props;

  // Internal state
  const [type, setType] = useState(typeFromProps);
  const [currency, setCurrency] = useState(currencyFromProps);
  const [searchTerm, setSearchTerm] = useState(searchTermFromProps);
  const [timeRange, setTimeRange] = useState(timeRangeFromProps);
  const [startDate, setStartDate] = useState(startDateFromProps);
  const [endDate, setEndDate] = useState(endDateFromProps);
  const [status, setStatus] = useState(statusFromProps);
  const [paymentChannel, setPaymentChannel] = useState(paymentChannelFromProps);
  const [dateFilterType, setDateFilterType] = useState(dateFilterTypeFromProps);

  const { t } = useTranslation('translations', { keyPrefix: 'AccountingSystem.SearchForm' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  // Update internal state when props change (for external reset)
  useEffect(() => {
    setType(typeFromProps);
  }, [typeFromProps]);

  useEffect(() => {
    setCurrency(currencyFromProps);
  }, [currencyFromProps]);

  useEffect(() => {
    setSearchTerm(searchTermFromProps);
  }, [searchTermFromProps]);

  useEffect(() => {
    setTimeRange(timeRangeFromProps);
  }, [timeRangeFromProps]);

  useEffect(() => {
    setStartDate(startDateFromProps);
  }, [startDateFromProps]);

  useEffect(() => {
    setEndDate(endDateFromProps);
  }, [endDateFromProps]);

  useEffect(() => {
    setStatus(statusFromProps);
  }, [statusFromProps]);

  useEffect(() => {
    setPaymentChannel(paymentChannelFromProps);
  }, [paymentChannelFromProps]);

  useEffect(() => {
    setDateFilterType(dateFilterTypeFromProps);
  }, [dateFilterTypeFromProps]);

  const TimeRanges = [
    { value: 'custom', label: t('TimeRanges.custom') },
    { value: 'today', label: t('TimeRanges.today') },
    { value: 'yesterday', label: t('TimeRanges.yesterday') },
    { value: 'thisWeek', label: t('TimeRanges.thisWeek') },
    { value: 'lastWeek', label: t('TimeRanges.lastWeek') },
    { value: 'thisMonth', label: t('TimeRanges.thisMonth') },
    { value: 'lastMonth', label: t('TimeRanges.lastMonth') },
    { value: 'thisQuarter', label: t('TimeRanges.thisQuarter') },
    { value: 'lastQuarter', label: t('TimeRanges.lastQuarter') },
    { value: 'last6Months', label: t('TimeRanges.last6Months') },
    { value: 'last12Months', label: t('TimeRanges.last12Months') },
  ];

  const paymentMethodOptions = [
    { value: 'EFT', label: t('PaymentMethod.Options.eft') },
    { value: 'ACH', label: t('PaymentMethod.Options.ach') },
    { value: 'Debit Card', label: t('PaymentMethod.Options.debitCard') },
    { value: 'Credit Card', label: t('PaymentMethod.Options.creditCard') },
    { value: 'Interac e-Transfer', label: 'Interac e-Transfer' },
    { value: 'VoPay Instant', label: 'VoPay Instant' },
  ];

  const currencyOptions = [
    { value: 'all', label: t('Currency.Options.all') },
    { value: 'CAD', label: 'CAD' },
    { value: 'USD', label: 'USD' },
  ];

  const statusOptions = [
    { value: 'all', label: t('Status.Options.all') },
    { value: 'partiallyPaid', label: t('Status.Options.partiallyPaid') },
    { value: 'open', label: t('Status.Options.open') },
  ];

  const paymentChannelOptions = [
    { value: 'all', label: t('PaymentChannel.Options.all') },
    { value: 'vopay', label: t('PaymentChannel.Options.vopay') },
    { value: 'erp', label: t('PaymentChannel.Options.erp') },
  ];

  // const dateFilterTypeOptions = [
  //   { value: 'closedDate', label: t('DateFilterType.Options.closedDate') },
  //   { value: 'dueDate', label: t('DateFilterType.Options.dueDate') },
  // ];

  return (
    <Box jc="sb" flx="col" gap="md">
      <Box gap="sm" wrap="wrap">
        {/* 
        TODO:Add Later
        {activeTab === 'closed' && (
          <Box width="175px">
            <Select
              version="2"
              placeholder={t('DateFilterType.placeholder')}
              options={dateFilterTypeOptions}
              name="dateFilterType"
              value={dateFilterType}
              onBlur={() => {}}
              onChange={(e) => {
                setDateFilterType(e.target.value as DateFilterType);
              }}
            />
          </Box>
        )} */}
        <Box width="175px">
          <Select
            version="2"
            placeholder={section === 'Receivable' ? t('dateReceivablePlaceholder') : t('datePlaceholder')}
            options={TimeRanges}
            name="date"
            value={timeRange}
            onBlur={() => {}}
            onChange={(e) => {
              setTimeRange(e.target.value as TimeRange);
            }}
          />
        </Box>
        {timeRange === 'custom' && (
          <DateRangePicker
            onChange={(range) => {
              setStartDate(range[0]);
              setEndDate(range[1]);
            }}
            allowBlank
            value={startDate && endDate ? [startDate, endDate] : null}
            triggerVariant="outlineShadow"
          />
        )}

        <Box width="200px">
          <MultiSelect
            name="type"
            value={type.split('|').filter((item) => item !== '')}
            placeholder={t('PaymentMethod.placeholder')}
            options={paymentMethodOptions}
            onChange={(e) => {
              const value = e.target.value;
              const cleanedPaymentMethods =
                value === ''
                  ? []
                  : value
                      .split(',')
                      .map((item) => item.trim())
                      .filter((item) => item !== '');
              setType(cleanedPaymentMethods.join('|'));
            }}
            onBlur={() => {}}
            sort={false}
          />
        </Box>
        <Box width="150px">
          <Select
            version="2"
            placeholder={t('Currency.placeholder')}
            options={currencyOptions}
            name="currency"
            value={currency}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrency(e.target.value as Currency);
            }}
          />
        </Box>

        {activeTab === 'open' && (
          <Box width="175px">
            <Select
              version="2"
              placeholder={t('Status.placeholder')}
              options={statusOptions}
              name="status"
              value={status}
              onBlur={() => {}}
              onChange={(e) => {
                setStatus(e.target.value as StatusFilter);
              }}
            />
          </Box>
        )}

        {activeTab === 'closed' && (
          <Box width="175px">
            <Select
              version="2"
              placeholder={t('PaymentChannel.placeholder')}
              options={paymentChannelOptions}
              name="paymentChannel"
              value={paymentChannel}
              onBlur={() => {}}
              onChange={(e) => {
                setPaymentChannel(e.target.value as PaymentChannel);
              }}
            />
          </Box>
        )}

        <TextField
          key="AccountingSoftwareKeyword"
          name="AccountingSoftwareKeyword"
          placeholder={t('KeywordPlaceholder')}
          value={searchTerm}
          onBlur={() => {}}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          variant="outlineShadow"
        />
        <Button
          id="search-accounting-software"
          size="small"
          colorScheme="primary"
          disabled={startDate === undefined || endDate === undefined}
          onClick={() => {
            onSearch({
              type,
              currency,
              searchTerm,
              timeRange,
              startDate,
              endDate,
              status,
              paymentChannel,
              dateFilterType,
            });
          }}
        >
          {tGlobal('Buttons.search')}
        </Button>
      </Box>
    </Box>
  );
};

export default AccountingSystemSearchForm;
